/**
 * AutopilotAdmin — agency-wide overview across all sessions.
 *
 * Use case (agency owner / super_admin): single pane to spot:
 *   - Sessions in 'wrong_pocket' or 'data_starved' pixel state
 *   - Sessions with pending alerts older than 24h (SLA breach)
 *   - Sessions with no decisions in last 12h (job health)
 *   - Sessions over 80% of monthly_budget_max (cost ceiling alert)
 *
 * NOT a per-session dashboard — that's pages/autopilot/dashboard.tsx.
 * This is the bird's-eye view for triage.
 */
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Activity, Clock, DollarSign, ChevronRight } from "lucide-react";
import { useAutopilotAdminTriage, type AdminTriageSession } from "@/hooks/useAutopilot";

const TRIAGE_REASON_LABELS: Record<string, string> = {
  pixel_data_starved: "Pixel: data starved (volume < TAM threshold)",
  pixel_wrong_pocket: "Pixel: wrong pocket (CAPI match score low)",
  no_decisions_12h: "Cron health: no decisions w ostatnich 12h",
  budget_over_80pct: "Budget: >80% monthly cap",
  alert_pending_24h: "Alert: pending >24h (SLA breach)",
  inventory_at_risk: "Inventory: <14 days stock (Theriot gate)",
};

export default function AutopilotAdmin() {
  const { data, isLoading } = useAutopilotAdminTriage();

  const sessions = data?.sessions ?? [];
  const critical = sessions.filter((s) => s.severity === "critical");
  const degraded = sessions.filter((s) => s.severity === "degraded");
  const healthy = sessions.filter((s) => s.severity === "healthy");

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-6 max-w-6xl space-y-6">
        <a
          href="/admin/autopilot"
          className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1"
        >
          ← Autopilot Ads
        </a>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Autopilot Admin — agency triage</h1>
            <p className="text-sm text-muted-foreground">
              Bird's-eye view across all sessions. Critical first, healthy last.
              {data && (
                <span className="ml-2">
                  Overall:{" "}
                  <Badge variant={data.overall === "critical" ? "destructive" : "outline"}>
                    {data.overall}
                  </Badge>
                </span>
              )}
            </p>
          </div>
          {data && (
            <span className="text-xs text-muted-foreground">
              Updated: {new Date(data.generatedAt).toLocaleString("pl-PL")}
            </span>
          )}
        </div>

        {isLoading && (
          <Card>
            <CardContent className="p-6 text-muted-foreground">Ładowanie…</CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Critical"
            value={critical.length}
            icon={AlertTriangle}
            color="bg-red-500"
          />
          <StatCard label="Degraded" value={degraded.length} icon={Clock} color="bg-yellow-500" />
          <StatCard label="Healthy" value={healthy.length} icon={Activity} color="bg-green-500" />
        </div>

        {critical.length > 0 && (
          <Card className="border-red-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-500">
                <AlertTriangle className="w-5 h-5" /> Critical ({critical.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SessionList items={critical} />
            </CardContent>
          </Card>
        )}

        {degraded.length > 0 && (
          <Card className="border-yellow-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-500">
                <Clock className="w-5 h-5" /> Degraded ({degraded.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SessionList items={degraded} />
            </CardContent>
          </Card>
        )}

        {healthy.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-500">
                <Activity className="w-5 h-5" /> Healthy ({healthy.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SessionList items={healthy} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: typeof Activity;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-3xl font-bold">{value}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
        <div className={`${color} p-3 rounded-full text-white`}>
          <Icon className="w-5 h-5" />
        </div>
      </CardContent>
    </Card>
  );
}

function SessionList({ items }: { items: ReadonlyArray<AdminTriageSession> }) {
  return (
    <div className="space-y-2">
      {items.map((s) => (
        <Link key={s.sessionId} href={`/admin/autopilot/${s.sessionId}`}>
          <div className="flex items-center justify-between border-b py-2 hover:bg-muted/30 cursor-pointer px-2">
            <div>
              <div className="font-medium">
                {s.industry ?? "—"} ({s.funnelType})
              </div>
              <div className="text-xs text-muted-foreground">
                {s.market} · {s.status}
                {s.flags.length > 0 && (
                  <span className="ml-2 inline-flex flex-wrap gap-1">
                    {s.flags.map((f, i) => (
                      <Badge
                        key={i}
                        variant={s.severity === "critical" ? "destructive" : "outline"}
                        className="text-[10px]"
                      >
                        {TRIAGE_REASON_LABELS[f.code] ?? f.message}
                      </Badge>
                    ))}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        </Link>
      ))}
    </div>
  );
}
